import type { ReactNode } from 'react';

// 定义功能特性类型
export interface Feature {
  name: string;
  free: ReactNode;
  standard: ReactNode;
  premium: ReactNode;
  ultimate: ReactNode;
  id: string;
}

// 定义表格分类类型
export interface FeatureCategory {
  title: string;
  features: Feature[];
  isOpen: boolean;
  id: string;
}

// 定义功能标记类型
export type FeatureValue = boolean | string | ReactNode;

// 定义翻译函数类型 - 使用更宽泛的类型以兼容 next-intl
type TranslationFunction = (...args: unknown[]) => string;

/**
 * 获取套餐功能对比数据
 * @param t - 国际化翻译函数
 * @param isChinaSite - 是否为中国站
 * @returns 功能分类数组
 */
export const getComparisonFeatures = (t: TranslationFunction, isChinaSite: boolean): FeatureCategory[] => [
  {
    id: 'video-generation',
    title: t('pricing.testimonials.comparisonTable.features.videoGeneration'),
    isOpen: true,
    features: [
      {
        id: 'video-1080p',
        name: t('pricing.testimonials.comparisonTable.features.video1080p'),
        free: isChinaSite ? t('pricing.testimonials.comparisonTable.features.videoFree') : false,
        standard: true,
        premium: true,
        ultimate: true,
      },
      {
        id: 'video-720p',
        name: t('pricing.testimonials.comparisonTable.features.video720p'),
        free: isChinaSite ? t('pricing.testimonials.comparisonTable.features.videoFree') : false,
        standard: true,
        premium: true,
        ultimate: true,
      },
      {
        id: 'video-8s',
        name: t('pricing.testimonials.comparisonTable.features.video6_8s'),
        free: false,
        standard: true,
        premium: true,
        ultimate: true,
      },
      {
        id: 'video-11-16s',
        name: t('pricing.testimonials.comparisonTable.features.video11_16s'),
        free: false,
        standard: true,
        premium: true,
        ultimate: true,
      },
      {
        id: 'q2-image-1080p-unlimited',
        name: t('pricing.testimonials.comparisonTable.features.q2Image1080pUnlimited'),
        free: false,
        standard: false,
        premium: false,
        ultimate: true,
      },
      {
        id: 'non-peak',
        name: t('pricing.testimonials.comparisonTable.features.nonPeak'),
        free: false,
        standard: false,
        premium: false,
        ultimate: true,
      },
      {
        id: 'fast-gen',
        name: t('pricing.testimonials.comparisonTable.features.fastGen'),
        free: false,
        standard: t('pricing.testimonials.comparisonTable.features.fastStandard'),
        premium: t('pricing.testimonials.comparisonTable.features.fastPremium'),
        ultimate: t('pricing.testimonials.comparisonTable.features.fastUltimate'),
      },
      {
        id: 'batch-gen',
        name: t('pricing.testimonials.comparisonTable.features.batchGen'),
        free: false,
        standard: t('pricing.testimonials.comparisonTable.features.batchStandard'),
        premium: t('pricing.testimonials.comparisonTable.features.batchPremium'),
        ultimate: t('pricing.testimonials.comparisonTable.features.batchUltimate'),
      },
      {
        id: 'ultra-hd',
        name: t('pricing.testimonials.comparisonTable.features.ultraHd'),
        free: false,
        standard: true,
        premium: true,
        ultimate: true,
      },
    ],
  },
  {
    id: 'content-management',
    title: t('pricing.testimonials.comparisonTable.features.contentManagement'),
    isOpen: true,
    features: [
      {
        id: 'my-references',
        name: t('pricing.testimonials.comparisonTable.features.myReferences'),
        free: t('pricing.testimonials.comparisonTable.features.myReferencesNumber', { n: 10 }),
        standard: t('pricing.testimonials.comparisonTable.features.myReferencesNumber', { n: 50 }),
        premium: t('pricing.testimonials.comparisonTable.features.myReferencesNumber', { n: 100 }),
        ultimate: t('pricing.testimonials.comparisonTable.features.myReferencesNumber', { n: 300 }),
      },
      {
        id: 'commercial-use',
        name: t('pricing.testimonials.comparisonTable.features.commercialUse'),
        free: false,
        standard: true,
        premium: true,
        ultimate: true,
      },
      ...(!isChinaSite
        ? [
            {
              id: 'no-watermark',
              name: t('pricing.testimonials.comparisonTable.features.noWatermark'),
              free: false,
              standard: true,
              premium: true,
              ultimate: true,
            },
          ]
        : []),
    ],
  },
  {
    id: 'service-support',
    title: t('pricing.testimonials.comparisonTable.features.serviceSupport'),
    isOpen: true,
    features: [
      {
        id: 'credit-purchase',
        name: t('pricing.testimonials.comparisonTable.features.creditPurchase'),
        free: false,
        standard: true,
        premium: true,
        ultimate: true,
      },
      {
        id: 'feedback',
        name: t('pricing.testimonials.comparisonTable.features.feedback'),
        free: false,
        standard: true,
        premium: true,
        ultimate: true,
      },
      {
        id: 'tutorials',
        name: t('pricing.testimonials.comparisonTable.features.onlineTutorials'),
        free: true,
        standard: true,
        premium: true,
        ultimate: true,
      },
    ],
  },
];
